import React, { useContext, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { ProductContainerContext } from '@rsc/contexts/ProductContainerContext';
import Image from '@rsc/ui-components/tailwind/Image';
import { Checkbox } from '@rsc/ui-components/shadcn/Checkbox';
import { Label } from '@rsc/ui-components/shadcn/Label';
import { shrinkProductImage } from '@rsc/utils';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import { CartContext } from '@rsc/contexts';
import { LocalizedLink } from '@rsc/gatsby-localization-helpers';

import {
  FETCH_PRODUCT_ADDON_QUERY,
  makeShopifyRequest,
} from '../../dept/utils/shopifyClient';

interface Props {
  itemKey: string;
  product: {
    SEOMetadata: {
      title: string;
    };
    store: {
      previewImageUrl: string;
      slug: {
        current: string;
      };
      title: string;
      variants: object[];
    };
    shopifyProduct: {
      productType: string;
      shopifyId: string;
      title: string;
      variants: {
        compareAtPrice: number;
        media: object[];
        price: number;
        selectedOptions: {
          name: string;
          value: string;
        }[];
        shopifyId: string;
        sku: string;
        title: string;
      }[];
      vendor: string;
    };
  };
  variant: {
    sku: string;
  };
}

const ProductAddOnCard = ({ itemKey, product, variant }: Props) => {
  // @ts-expect-error until context is converted to TS...KA
  const { addOns, setAddOns } = useContext(ProductContainerContext);
  const { tt, localizePrice } = useUiComponentsInternationalContext();
  // @ts-expect-error until context is converted to TS
  const { shopifyClient } = useContext(CartContext);

  const [fullVariant, setFullVariant] = useState(null);

  useEffectOnce(() => {
    // guard against missing/archived sanity data
    if (!product.shopifyProduct) {
      return;
    }

    const getShopifyData = async () => {
      const updatedProduct = await makeShopifyRequest(
        FETCH_PRODUCT_ADDON_QUERY,
        { id: product.shopifyProduct.shopifyId },
        shopifyClient
      );

      const sVariant = updatedProduct?.product?.variants?.nodes.find(
        ({ sku }) => sku === variant?.sku
      );
      const pVariant = product.shopifyProduct.variants?.find(
        ({ sku }) => sku === variant?.sku
      );

      if (sVariant && pVariant) {
        const cartProps = {
          variantId: pVariant.shopifyId,
          quantity: 1,
          brand: product?.shopifyProduct?.vendor,
          category: product?.shopifyProduct?.productType,
          title: product?.shopifyProduct?.title,
          variantTitle: pVariant?.title,
        };

        const sv = {
          ...sVariant, // ! order important for product addons on pdp titles
          ...pVariant,
          cartProps,
          imageUrl: product.store.previewImageUrl,
          _key: itemKey,
          title: product.store.title,
          slug: product.store.slug.current,
        };

        setFullVariant(sv);
      }
    };

    getShopifyData();
  });

  if (!fullVariant) return null;

  const outOfStock =
    !fullVariant.availableForSale || fullVariant.quantityAvailable < 1;

  return (
    <div className="flex items-center w-full gap-2 p-4 rounded-lg bg-muted">
      <Checkbox
        aria-label={tt(
          s => s.productOptionsComponent.productAddOnCheckboxAreaLabel
        )}
        checked={addOns.some(ao => ao.sku === variant.sku)}
        disabled={outOfStock}
        id={`${itemKey}-checkbox`}
        onCheckedChange={e => {
          setAddOns(aos => {
            if (e) return [...aos, fullVariant];
            return aos.filter(ao => ao.sku !== variant.sku);
          });
        }}
      />
      <Label
        className="flex items-center gap-2"
        htmlFor={`${itemKey}-checkbox`}
      >
        <Image
          alt={
            product.SEOMetadata?.title ||
            product.store?.title ||
            'Product image'
          }
          className="min-w-16"
          src={shrinkProductImage(fullVariant.imageUrl, 64)}
          width="16"
        />
        <div className="flex flex-col flex-start">
          {outOfStock && (
            <span className="text-sm text-foreground">Out of Stock</span>
          )}
          <LocalizedLink
            className="text-sm text-muted-foreground-link font-bold no-underline hover:underline"
            to={`/product/${fullVariant.slug}`}
          >
            {fullVariant.title}
          </LocalizedLink>
          <span className="flex gap-4 text-sm text-foreground font-bold">
            {localizePrice(fullVariant.price)}
            <span className="line-through">
              {fullVariant.compareAtPrice
                ? localizePrice(fullVariant.compareAtPrice)
                : ''}
            </span>
          </span>
        </div>
      </Label>
    </div>
  );
};

export default ProductAddOnCard;
